import { BackendGrade, Grade } from './Grade';
import { Group } from './Group';
import { BackendUser, mapBackendUserToUser, User } from './User';
import { Score } from './Score';

export type ProgressPageType = 'theory' | 'opbouw';

export type ProgressPage = {
  id: string;
  title: string;
  type: ProgressPageType;
  blocks: string[];
};

export type TrajectoryStudentScoreProgressOfType = {
  hard: TrajectoryStudentScoreProgressOfExerciseType;
  average: TrajectoryStudentScoreProgressOfExerciseType;
  easy: TrajectoryStudentScoreProgressOfExerciseType;
  opbouw: TrajectoryStudentScoreProgressOfPageType;
  theory: TrajectoryStudentScoreProgressOfPageType;
};

export type TrajectoryStudentScoreProgressOfExerciseType = {
  done: string | 0;
  percentage: number;
};

export type TrajectoryStudentScoreProgressOfPageType = {
  done: string | 0;
  percentage: number;
  completed: boolean;
};

export type AdditionalProgress = {
  averageScoreProgressOfType: {
    hard: ScoreProgressItem & Score;
    average: ScoreProgressItem & Score;
    easy: ScoreProgressItem & Score;
    opbouw: ScoreProgressItem & Score;
    theory: ScoreProgressItem & Score;
  };
  trajectoryStudentScoreProgressOfType: Record<string, TrajectoryStudentScoreProgressOfType>;
};

export type StudentProgress = {
  completedAt: string | null;
  createdAt: string | null;
  trajectoryId: string;
  page: ProgressPage;
  user?: User;
};

export type GroupProgress = {
  type: ProgressPageType;
  completed: number;
  total: number;
};

export type StudentWithProgress = {
  id: string;
  firstName: string;
  name: string;
  trajectoryProgress?: TrajectoryProgress[];
  additionalProgress?: AdditionalProgress;
  isOutdated: boolean;
};

export type GroupWithProgress = Omit<Group, 'grade' | 'schoolSubjects'> & {
  students?: StudentWithProgress[];
  additionalProgress?: AdditionalProgress;
  isOutdated: boolean;
};

export type GradeWithProgress = Grade & {
  groups: GroupWithProgress[];
};

export type ScoreProgressType = 'easy' | 'average' | 'hard';

export type ScoreProgressItem = {
  completed: number;
  total: number;
  progress: number;
};

export type ScoreProgress = {
  hard: Record<string, ScoreProgressItem>;
  average: Record<string, ScoreProgressItem>;
  easy: Record<string, ScoreProgressItem>;
  opbouw?: Record<string, ScoreProgressItem>;
  theory?: Record<string, ScoreProgressItem>;
};

export type TrajectoryProgress = {
  trajectoryId: string;
  parentId?: string;
  createdAt: string;
  completedAt: string;
};

// Backend

export type BackendStudentProgress = Omit<
  StudentProgress,
  'completedAt' | 'createdAt' | 'trajectoryId'
> & {
  completed_at: string;
  created_at: string;
  trajectory_id: string;
  page: {
    data: ProgressPage;
  };
  user: {
    data: BackendUser;
  };
};

export type BackendTrajectoryProgress = {
  completed_at: string;
  created_at: string;
  trajectory_id: string;
  parent_id?: string;
};

type BackendStudentWithProgress = Omit<
  StudentWithProgress,
  'additionalProgress' | 'trajectoryProgress' | 'isOutdated'
> & {
  additional_progress: AdditionalProgress;
  trajectory_progress: {
    data: BackendTrajectoryProgress[];
  };
  is_outdated: boolean;
};

type BackendGroupWithProgress = Omit<
  GroupWithProgress,
  'additionalProgress' | 'isDefault' | 'grade' | 'students' | 'isOutdated'
> & {
  additional_progress: AdditionalProgress;
  is_default: 0 | 1;
  grade: {
    data: BackendGrade;
  };
  students: {
    data: BackendStudentWithProgress[];
  };
  is_outdated: boolean;
};

// MAPPERS

export const mapBackendStudentProgressToStudentProgress = (
  data: BackendStudentProgress
): StudentProgress => ({
  completedAt: data.completed_at,
  createdAt: data.created_at,
  trajectoryId: data.trajectory_id,
  page: data.page.data,
  user: data.user ? mapBackendUserToUser(data.user.data) : undefined,
});

const mapBackendStudentWithProgressToStudentWithProgress = (
  data: BackendStudentWithProgress
): StudentWithProgress => ({
  id: data.id,
  firstName: data.firstName,
  name: data.name,
  trajectoryProgress: data.trajectory_progress.data.map(
    mapBackendTrajectoryProgressToTrajectoryProgress
  ),
  additionalProgress: data.additional_progress,
  isOutdated: data.is_outdated,
});

const mapBackendGroupWithProgressToGroupWithProgress = (
  data: BackendGroupWithProgress
): GroupWithProgress => ({
  id: data.id,
  name: data.name,
  description: data.description,
  isDefault: Boolean(data.is_default),
  students: data.students.data.map(mapBackendStudentWithProgressToStudentWithProgress),
  additionalProgress: data.additional_progress,
  isOutdated: data.is_outdated,
});

const mapBackendTrajectoryProgressToTrajectoryProgress = (
  data: BackendTrajectoryProgress
): TrajectoryProgress => ({
  completedAt: data.completed_at,
  createdAt: data.created_at,
  trajectoryId: data.trajectory_id,
  parentId: data.parent_id,
});

export const getGroupProgressByChapterMapper = (response: any): GroupWithProgress =>
  mapBackendGroupWithProgressToGroupWithProgress(response.data);

export const getUserProgressByChapterMapper = (response: any): StudentWithProgress =>
  mapBackendStudentWithProgressToStudentWithProgress(response.data);
